import type { ReactNode } from 'react';
import { Button } from './Button';
import { cn } from '@/lib/utils/cn';

type EmptyStateProps = {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** Visual tone. `error` shifts the title color and surfaces a Retry button when `onRetry` is provided. */
  tone?: 'neutral' | 'error';
  onRetry?: () => void;
};

export function EmptyState({
  title,
  description,
  action,
  tone = 'neutral',
  onRetry,
}: EmptyStateProps) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className="container-prose py-20 text-center"
    >
      <h2
        className={cn(
          'text-xl sm:text-2xl mb-3',
          tone === 'error'
            ? 'text-ink-900 dark:text-ink-50'
            : 'text-ink-800 dark:text-ink-100',
        )}
      >
        {title}
      </h2>
      {description && (
        <p className="text-ink-500 dark:text-ink-400 leading-relaxed">
          {description}
        </p>
      )}
      {(onRetry || action) && (
        <div className="mt-6 flex justify-center gap-3">
          {onRetry && (
            <Button variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
